import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Package,
  DollarSign,
  FolderOpen,
  Image as ImageIcon,
  AlertTriangle,
  Tag,
  Check,
  Flame,
  Star,
  Sparkles,
  Leaf,
  Salad,
  Save,
  ChevronDown,
  ChevronUp,
  Settings2
} from 'lucide-react'
import { useMenuStore } from '@/stores/menuStore'
import type { Product, Category, ProductCustomizationOptions } from '@/types'
import TranslationEditor from './TranslationEditor'
import CustomizationOptionsEditor from './CustomizationOptionsEditor'

interface ProductFormProps {
  product: Product | null
  restaurantId: string
  categories: Category[]
  onClose: () => void
}

export default function ProductForm({ product, restaurantId, categories, onClose }: ProductFormProps) {
  const { createProduct, updateProduct, isLoading } = useMenuStore()

  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || 0,
    categoryId: product?.categoryId || (categories[0]?.id || ''),
    imageUrl: product?.imageUrl || '',
    allergens: product?.allergens?.join(', ') || '',
    tags: product?.tags?.join(', ') || '',
    available: product?.available ?? true,
    translations: product?.translations || {},
    customizationOptions: product?.customizationOptions || null,
    // Badge flags
    isPopular: product?.isPopular ?? false,
    isNew: product?.isNew ?? false,
    isChefRecommended: product?.isChefRecommended ?? false,
    isVegetarian: product?.isVegetarian ?? false,
    isVegan: product?.isVegan ?? false,
  })

  // Collapsible sections state
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showCustomization, setShowCustomization] = useState(!!product?.customizationOptions)
  const [showTranslations, setShowTranslations] = useState(false)

  const isEditing = !!product

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseFloat(value) : value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Parse comma-separated strings to arrays
    const allergens = formData.allergens
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)

    const tags = formData.tags
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)

    const productData = {
      name: formData.name,
      description: formData.description,
      price: formData.price,
      categoryId: formData.categoryId,
      imageUrl: formData.imageUrl || undefined,
      allergens,
      tags,
      available: formData.available,
      translations: formData.translations,
      customizationOptions: formData.customizationOptions,
      // Badge flags
      isPopular: formData.isPopular,
      isNew: formData.isNew,
      isChefRecommended: formData.isChefRecommended,
      isVegetarian: formData.isVegetarian,
      isVegan: formData.isVegan,
    }

    try {
      if (isEditing && product) {
        await updateProduct(product.id, productData)
      } else {
        await createProduct(restaurantId, productData)
      }
      onClose()
    } catch (error) {
      // Error handled by store
    }
  }

  // Badge configuration
  const badges = [
    { key: 'isPopular', label: 'Più Venduto', icon: Flame, color: 'orange', emoji: '🔥' },
    { key: 'isChefRecommended', label: 'Consigliato', icon: Star, color: 'purple', emoji: '⭐' },
    { key: 'isNew', label: 'Novità', icon: Sparkles, color: 'blue', emoji: '🆕' },
    { key: 'isVegan', label: 'Vegano', icon: Leaf, color: 'green', emoji: '🌱' },
    { key: 'isVegetarian', label: 'Vegetariano', icon: Salad, color: 'lime', emoji: '🥬' },
  ]

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.3 }}
          className="bg-white dark:bg-[#1e1e24] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10 flex items-center justify-between bg-gray-50 dark:bg-[#25252d]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {isEditing ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-white/50">
                  {isEditing ? 'Aggiorna i dettagli del prodotto' : 'Aggiungi un nuovo prodotto al menu'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-white/50" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <form id="product-form" onSubmit={handleSubmit} className="space-y-6">

              {/* Basic Info Section */}
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                    Nome Prodotto *
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#25252d] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                    placeholder="es. Carbonara"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                    Descrizione
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#25252d] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all resize-none"
                    placeholder="Descrivi il piatto, ingredienti principali..."
                  />
                </div>

                {/* Price & Category Row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Prezzo *
                    </label>
                    <div className="relative">
                      <input
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={formData.price}
                        onChange={handleChange}
                        className="w-full pl-8 pr-4 py-3 bg-gray-50 dark:bg-[#25252d] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                        placeholder="0.00"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40">€</span>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                      <FolderOpen className="w-4 h-4 inline mr-1" />
                      Categoria *
                    </label>
                    <select
                      name="categoryId"
                      required
                      value={formData.categoryId}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-[#25252d] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all appearance-none cursor-pointer"
                    >
                      {categories.length === 0 ? (
                        <option value="">Nessuna categoria</option>
                      ) : (
                        categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.icon} {category.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Image URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                    <ImageIcon className="w-4 h-4 inline mr-1" />
                    URL Immagine
                  </label>
                  <input
                    name="imageUrl"
                    type="url"
                    value={formData.imageUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#25252d] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                    placeholder="https://esempio.com/immagine.jpg"
                  />
                </div>

                {/* Availability Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#25252d] rounded-xl border border-gray-200 dark:border-white/10">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Disponibile</p>
                    <p className="text-sm text-gray-500 dark:text-white/50">Mostra nel menu pubblico</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, available: !formData.available })}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      formData.available ? 'bg-green-500' : 'bg-gray-300 dark:bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                      formData.available ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Badges Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Badge Prodotto
                </h3>
                <div className="flex flex-wrap gap-2">
                  {badges.map((badge) => {
                    const isActive = formData[badge.key as keyof typeof formData]
                    return (
                      <button
                        key={badge.key}
                        type="button"
                        onClick={() => setFormData({ ...formData, [badge.key]: !isActive })}
                        className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 text-sm font-medium ${
                          isActive
                            ? `border-${badge.color}-500 bg-${badge.color}-500/10 text-${badge.color}-600 dark:text-${badge.color}-400`
                            : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-white/50 hover:border-gray-300 dark:hover:border-white/20'
                        }`}
                        style={isActive ? {
                          borderColor: badge.color === 'orange' ? '#f97316' :
                                      badge.color === 'purple' ? '#a855f7' :
                                      badge.color === 'blue' ? '#3b82f6' :
                                      badge.color === 'green' ? '#22c55e' :
                                      badge.color === 'lime' ? '#84cc16' : '#f97316',
                          backgroundColor: badge.color === 'orange' ? 'rgba(249, 115, 22, 0.1)' :
                                          badge.color === 'purple' ? 'rgba(168, 85, 247, 0.1)' :
                                          badge.color === 'blue' ? 'rgba(59, 130, 246, 0.1)' :
                                          badge.color === 'green' ? 'rgba(34, 197, 94, 0.1)' :
                                          badge.color === 'lime' ? 'rgba(132, 204, 22, 0.1)' : 'rgba(249, 115, 22, 0.1)',
                          color: badge.color === 'orange' ? '#ea580c' :
                                badge.color === 'purple' ? '#9333ea' :
                                badge.color === 'blue' ? '#2563eb' :
                                badge.color === 'green' ? '#16a34a' :
                                badge.color === 'lime' ? '#65a30d' : '#ea580c'
                        } : {}}
                      >
                        <span>{badge.emoji}</span>
                        {badge.label}
                        {isActive && <Check className="w-4 h-4" />}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Advanced Section - Collapsible */}
              <div className="border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-[#25252d] hover:bg-gray-100 dark:hover:bg-[#2a2a32] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-gray-500 dark:text-white/50" />
                    <span className="font-medium text-gray-900 dark:text-white">Opzioni Avanzate</span>
                    <span className="text-xs text-gray-500 dark:text-white/40">Allergeni, Tag</span>
                  </div>
                  {showAdvanced ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                <AnimatePresence>
                  {showAdvanced && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-4 border-t border-gray-200 dark:border-white/10">
                        {/* Allergens */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            Allergeni
                          </label>
                          <input
                            name="allergens"
                            type="text"
                            value={formData.allergens}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white dark:bg-[#1e1e24] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                            placeholder="glutine, uova, lattosio (separati da virgola)"
                          />
                        </div>

                        {/* Tags */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                            <Tag className="w-4 h-4 inline mr-1" />
                            Tag
                          </label>
                          <input
                            name="tags"
                            type="text"
                            value={formData.tags}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white dark:bg-[#1e1e24] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                            placeholder="tradizionale, piccante, senza glutine"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Customization Options - Collapsible */}
              <div className="border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCustomization(!showCustomization)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-[#25252d] hover:bg-gray-100 dark:hover:bg-[#2a2a32] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-gray-500 dark:text-white/50" />
                    <span className="font-medium text-gray-900 dark:text-white">Personalizzazioni</span>
                    <span className="text-xs text-gray-500 dark:text-white/40">Varianti, aggiunte</span>
                  </div>
                  {showCustomization ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                <AnimatePresence>
                  {showCustomization && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 border-t border-gray-200 dark:border-white/10">
                        <CustomizationOptionsEditor
                          value={formData.customizationOptions}
                          onChange={(customizationOptions) =>
                            setFormData({ ...formData, customizationOptions })
                          }
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Translations - Collapsible */}
              <div className="border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowTranslations(!showTranslations)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-[#25252d] hover:bg-gray-100 dark:hover:bg-[#2a2a32] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🌍</span>
                    <span className="font-medium text-gray-900 dark:text-white">Traduzioni</span>
                    <span className="text-xs text-gray-500 dark:text-white/40">Multilingua</span>
                  </div>
                  {showTranslations ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                <AnimatePresence>
                  {showTranslations && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 border-t border-gray-200 dark:border-white/10">
                        <TranslationEditor
                          type="product"
                          translations={formData.translations}
                          onChange={(translations) => setFormData({ ...formData, translations })}
                          supportedLanguages={['en', 'fr', 'de', 'es']}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </form>
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#25252d] flex items-center justify-between">
            {categories.length === 0 && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                Crea prima una categoria
              </p>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white/70 font-medium hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                form="product-form"
                disabled={isLoading || categories.length === 0}
                className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditing ? 'Aggiorna' : 'Crea Prodotto'}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
